import React, { useState, useEffect } from "react";
import Image from "../images/Image";
import SidebarItem from "./SidebarItem";
import { sidebarObj } from "./SidebarObj";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import usePrivilege from "../CustomHooks/usePrivilege";

interface DropdownItem {
  option: string;
  path: string;
  privilege: string;
}

interface SidebarItemType {
  label: string;
  dropdown?: string[] | DropdownItem[];
  link?: string;
  privileges?: string | string[];
}

interface ActiveTabState {
  activeTab: string;
  dropdownOpen: boolean;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { privileges } = useSelector((state: RootState) => state.privileges);
  const hasListMDPrivilege = usePrivilege('MDM1')
  const hasAddMDPrivilege = usePrivilege('MDM4')
  const [isActiveTab, setIsActiveTab] = useState<ActiveTabState>({
    activeTab: location.pathname,
    dropdownOpen: false,
  });
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    // Initialize dropdown options state
    const initializeDropdownOptions = () => {
      const optionsMap: Record<string, boolean> = {};
      
      // Process all items in sidebarObj
      sidebarObj.forEach(item => {
        // Handle dropdown items
        if (Array.isArray(item.dropdown)) {
          item.dropdown.forEach(dropdownItem => {
            // Handle object type dropdown items with 'option' property
            if (typeof dropdownItem === 'object' && 'option' in dropdownItem) {
              optionsMap[dropdownItem.option] = false;
            }
            // Handle string type dropdown items
            else if (typeof dropdownItem === 'string') {
              optionsMap[dropdownItem] = false;
            }
          });
        }
      });
      
      setDropdownOptions(optionsMap);
    };
    
    initializeDropdownOptions();
  }, []);
  
  useEffect(() => {
    setIsActiveTab((prev) => ({
      ...prev,
      activeTab: location.pathname,
    }));
  }, [location]);

  const checkPrivileges = (itemPrivileges: string | string[] | undefined, itemLabel?: string): boolean => {
    // Always show Dashboard regardless of privileges
    if (itemLabel === 'Dashboard') {
      return true;
    }
    
    // Return false if no privileges are specified
    if(privileges !== 'superadmin' && itemPrivileges === 'admin-allowed') {
      return true;
    }
    if(privileges === 'superadmin' && itemPrivileges === 'admin-allowed') {
      return false;
    }
    if(privileges === 'superadmin' && itemPrivileges === 'supper-admin') {
      return true;
    }
    if (!itemPrivileges || !privileges) {
      return false;
    }
    if(privileges === 'superadmin'){
      return true;
    }
    // Handle 'allowed' case
    if (itemPrivileges === 'allowed') {
      return true;
    }

    // Handle array of privileges
    if (Array.isArray(itemPrivileges)) {
      return itemPrivileges.some(privilege => privileges.includes(privilege));
    }
    // Handle single privilege string
    return privileges.includes(itemPrivileges);
  };

  const renderSidebarItems = () => {
    return sidebarObj
      .filter(item => item !== undefined)
      .map((item, index) =>
        checkPrivileges(item.privileges, item.label) ? (
          <SidebarItem
            key={`${item.label}-${index}`}
            item={item}
            isActiveTab={isActiveTab}
            setIsActiveTab={setIsActiveTab}
            hasADDMDPrivilege={hasAddMDPrivilege}
            hasListMDPrivilege={hasListMDPrivilege}
            dropdownOptions={dropdownOptions}
            setDropdownOptions={setDropdownOptions}
          />
        ) : null
      );
  };

  return (
    <div className="bg-white w-full h-screen px-3 transition-transform duration-200 ease-in-out">
      <div 
        className="flex items-center justify-center pt-[40px] mb-2.5 cursor-pointer"
        onClick={() => navigate("/dashboard")}
      >
        <Image src="zood_pay_sidebar_img" />
      </div>
      <ul className={`overflow-y-scroll overflow-x-hidden ${getFirefoxCssFix('scroll')} scroll-auto h-scrollbarheight`}>
        {renderSidebarItems()}
      </ul>
    </div>
  );
};

export default Sidebar;